import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ListOrdered, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errMsg, timeAgo, useApi } from "@/lib/api";
import { DeployBadge } from "./Badges";
import { duration } from "./helpers";

function Row({ d, onCancel }) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 text-sm" data-testid={`queue-item-${d.id}`}>
      <span className="w-6 text-xs tabular-nums text-slate-400">{d.position ? `${d.position}°` : ""}</span>
      <Link to={`/sites/${d.site_id}`} className="min-w-0 flex-1 truncate font-medium text-slate-900 hover:text-sky-700">{d.site_name} <span className="font-normal text-slate-500">· n. {d.number}</span></Link>
      <span className="text-xs text-slate-500">{d.client_name} · {d.trigger_label}</span>
      <DeployBadge d={d} testid={`queue-status-${d.id}`} />
      {onCancel && <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-500" onClick={() => onCancel(d)} disabled={d.cancel_requested} data-testid={`queue-cancel-${d.id}`}><X size={13} className="mr-1" />Annulla</Button>}
    </li>
  );
}

export default function QueuePanel({ refreshKey, onSettled }) {
  const api = useApi();
  const [q, setQ] = useState(null);
  const active = useRef(0);
  const load = useCallback(() => api.get("/site-queue").then(({ data }) => {
    if (active.current && !data.active.length) onSettled?.();
    active.current = data.active.length;
    setQ(data);
  }).catch(() => {}), [api, onSettled]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const t = setInterval(load, q?.active.length ? 2500 : 10000);
    return () => clearInterval(t);
  }, [load, q?.active.length]);
  const cancel = async (d) => {
    try { await api.post(`/sites/${d.site_id}/deploys/${d.id}/cancel`); toast.success(`Compilazione n. ${d.number} annullata`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!q) return null;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="queue-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="inline-flex items-center gap-2 text-base font-semibold text-slate-900"><ListOrdered size={17} className="text-sky-700" />Coda di pubblicazione</h2>
        <p className="text-xs text-slate-500" data-testid="queue-stats">{q.stats.building} in compilazione · {q.stats.queued} in attesa · {q.stats.instances} {q.stats.instances === 1 ? "processo" : "processi"} del server</p>
      </div>
      {q.active.length ? (
        <ul className="mt-2 divide-y divide-slate-100" data-testid="queue-active">{q.active.map((d) => <Row key={d.id} d={d} onCancel={cancel} />)}</ul>
      ) : <p className="mt-2 text-sm text-slate-500" data-testid="queue-empty">Nessuna compilazione in corso. Le compilazioni partono da sole, una alla volta per sito.</p>}
      {q.recent.length > 0 && (
        <details className="mt-3 group">
          <summary className="cursor-pointer text-xs font-medium text-slate-500 hover:text-slate-800" data-testid="queue-recent-toggle">Ultime compilazioni terminate</summary>
          <ul className="mt-1 divide-y divide-slate-100" data-testid="queue-recent">
            {q.recent.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-x-3 py-2 text-xs text-slate-600">
                <Link to={`/sites/${d.site_id}`} className="min-w-0 flex-1 truncate text-slate-800 hover:text-sky-700">{d.site_name} · n. {d.number}</Link>
                <span>{timeAgo(d.finished_at)} · {duration(d.started_at, d.finished_at)}</span><DeployBadge d={d} />
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
